import EmptyState from "@/components/empty-state/EmptyState.tsx";
import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import {
  ConfirmationModal,
  Page,
  TabItem,
  Tabs,
  useModals,
  Avatar,
  Button,
  DropdownMenu,
  FontAwesomeIcon,
  ListItem,
  Stack,
  Table,
  TableCell,
} from "@skailer-com/uikit";
import { getTextPreview } from "@/helpers/utils.helper.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { TemplateEditModal } from "@/pages/settings/recruit/templates/widgets/TemplateEditModal.tsx";
import { useTemplateDelete, useTemplates } from "@/queries/use-templates.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { ApiTemplate, ApiTemplateType } from "@/types/template.types.ts";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";

export default function Templates() {
  const { t } = useTranslation();
  const { renderEmployeeName } = useFormatter();

  useTitle(t("common:templates"), t("common:settings"));

  const { openModal } = useModals();
  const { data: templates, isLoading, isFetching } = useTemplates();
  const emailTemplateDelete = useTemplateDelete();

  const publicEmailTemplates = templates?.filter((emailTemplate) => !emailTemplate.is_private);
  const privateEmailTemplates = templates?.filter((emailTemplate) => emailTemplate.is_private);

  const handleView = useCallback((emailTemplate: ApiTemplate) => {
    openModal(TemplateEditModal, {
      emailTemplate: emailTemplate,
      readOnly: true,
    });
  }, []);

  const handleEdit = useCallback((emailTemplate: ApiTemplate) => {
    openModal(TemplateEditModal, {
      emailTemplate: emailTemplate,
    });
  }, []);

  const handleCreate = useCallback((templateType: ApiTemplateType) => {
    openModal(TemplateEditModal, {
      templateType: templateType,
    });
  }, []);

  const handleDelete = useCallback((emailTemplate: ApiTemplate) => {
    openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("common:delete_confirm"),
      acceptTitle: t("common:delete"),
      onAccept: async function () {
        try {
          await emailTemplateDelete.mutateAsync(emailTemplate.id, {
            onSuccess: () => {
              analyticsService.trackEvent(analyticEvents.templates.deleted, {
                [analyticProperties.id]: emailTemplate.id,
              });
            },
          });
        } catch {}
      },
    });
  }, []);

  const columns = useMemo<ColumnDef<ApiTemplate>[]>(
    () => [
      {
        header: t("common:title"),
        cell: (row) => (
          <ListItem
            icon={
              <Avatar
                icon={
                  <FontAwesomeIcon
                    icon={`fa-light ${row.row.original.template_type == ApiTemplateType.email ? "fa-envelope" : "fa-paper-plane"}`}
                  />
                }
              />
            }
            title={row.row.original.title}
            caption={getTextPreview(row.row.original.body, 200)}
            forceShowAvatar={true}
            truncate={true}
            behavior={"card"}
            onClick={() => {
              if (row.row.original.can_edit) {
                handleEdit(row.row.original);
              } else {
                handleView(row.row.original);
              }
            }}
          />
        ),
        ...ColumnSizes.employee,
        maxSize: 700,
      },
      {
        header: t("common:type"),
        cell: (row) => (
          <TableCell caption={renderEmployeeName(row.row.original.owner)}>
            {row.row.original.template_type == ApiTemplateType.email
              ? t("core:email_template")
              : t("core:message_template")}
          </TableCell>
        ),
        ...ColumnSizes.status,
        maxSize: 500,
      },
    ],
    [templates]
  );

  const renderRowAction = useCallback((row: Row<ApiTemplate>) => {
    const template = row.original;
    return (
      <DropdownMenu>
        {template.can_edit ? (
          <>
            <Button
              onClick={() => handleEdit(template)}
              leftIcon={<FontAwesomeIcon icon={"fa-light fa-pen"} />}
              variant={"menu"}
            >
              {t("common:edit")}
            </Button>
            <Button
              onClick={() => handleDelete(template)}
              leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
              variant={"menuDanger"}
            >
              {t("common:delete")}...
            </Button>
          </>
        ) : (
          <Button
            onClick={() => handleView(template)}
            leftIcon={<FontAwesomeIcon icon={"fa-light fa-eye"} />}
            variant={"menu"}
          >
            {t("common:view")}
          </Button>
        )}
      </DropdownMenu>
    );
  }, []);

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.templates.viewed);
  });

  return (
    <>
      <Page>
        <Page.Header showBack={true} title={t("common:templates")} navigateTo={"/settings"}>
          <Stack>
            <DropdownMenu
              trigger={
                <Button leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}>
                  {t("common:create_new")}
                </Button>
              }
            >
              <Stack>
                <Button
                  onClick={() => {
                    handleCreate(ApiTemplateType.email);
                  }}
                  leftIcon={<FontAwesomeIcon icon="fa-light fa-envelope" />}
                  variant={"menu"}
                >
                  {t("core:email_template")}
                </Button>
                <Button
                  onClick={() => {
                    handleCreate(ApiTemplateType.messenger);
                  }}
                  leftIcon={<FontAwesomeIcon icon="fa-light fa-paper-plane" />}
                  variant={"menu"}
                >
                  {t("core:message_template")}
                </Button>
              </Stack>
            </DropdownMenu>
          </Stack>
        </Page.Header>
        <Page.Content>
          <Tabs>
            <TabItem title={t("core:email_template_my")}>
              <Table
                layout={"fixed"}
                enableColumnResizing={true}
                renderRowAction={renderRowAction}
                data={privateEmailTemplates || []}
                columns={columns}
                showSkeleton={isLoading}
                isFetching={isFetching || emailTemplateDelete.isPending}
                notFoundComponent={<EmptyStateNothingFound />}
                emptyStateComponent={<EmptyState />}
              />
            </TabItem>
            <TabItem title={t("core:email_template_shared")}>
              <Table
                layout={"fixed"}
                enableColumnResizing={true}
                renderRowAction={renderRowAction}
                data={publicEmailTemplates || []}
                columns={columns}
                showSkeleton={isLoading}
                isFetching={isFetching || emailTemplateDelete.isPending}
                notFoundComponent={<EmptyStateNothingFound />}
                emptyStateComponent={<EmptyState />}
              />
            </TabItem>
          </Tabs>
        </Page.Content>
      </Page>
    </>
  );
}
